// React Components
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

// Ant Design Components
import { CheckOutlined, EditFilled, EditOutlined, InfoCircleOutlined, LinkOutlined, UserOutlined } from '@ant-design/icons';
import { DatePicker, notification, Tooltip } from 'antd';

// Utilities & Helpers
import { toISTDate } from '../../../../../../apis/constants';
import Helpers from '../../../../../../apis/helpers';
import dayjs from 'dayjs';

// UI Components
import H3Text from '../../../../../../uilib/h3Text';
import ViewCustomFields from '@Components/Common/CustomField/ViewCustomFields';
import PRZSelect from '../../../../../Common/UI/PRZSelect';
import { textTruncate } from '../../../../../Common/UI/PRZHelper/helpers';

const getRatingColor = (rating) => {
  if (rating >= 3) {
    return 'green'; // 3, 4, and 5 are green
  } if (rating >= 2) {
    return 'yellow'; // 2 and 3 are yellow
  } if (rating > 0) return 'red';
  return 'grey'; // 0 and 1 are red
};

const ViewGRNDetails = ({
  selectedGRN, user, updateInvoiceDate, quickUpdateGRN, quickUpdateGRNLoading, updatedGstNumber, updateInvoiceNumber, updateEWayBillNumber, getActivityLog, updateDueDate, eWayBillAttachment, isApInvoiceEnabled, getGRNById, selectedGrnId, match, setState, fileInputRef, handleFileChangeEWayBill,
}) => {
  const [isGstNumberEditable, setIsGstNumberEditable] = useState(false);
  const [isInvoiceNumber, setIsInvoiceNumber] = useState(false);
  const [isEWayBillNumber, setIsEWayBillNumber] = useState(false);
  const [isEWayBillAttachment, setIsEWayBillAttachment] = useState(false);

  const quickUpdateDisable = !(['VOID', 'REJECTED']?.includes(selectedGRN?.status)) && Helpers.getPermission(Helpers.permissionEntities.GOOD_RECEIVING, Helpers.permissionTypes.UPDATE, user);

  const accountingGSTTransactionAsPerMaster = user?.tenant_info?.purchase_config?.sub_modules?.vendor?.settings?.gst_details_in_transaction === 'AS_PER_MASTER';

  const isTallyConnected = (selectedGRN?.tenant_info?.tally_configuration?.purchase_voucher_integration) && (['ISSUED', 'VOID'].includes(selectedGRN?.status)) && (selectedGRN?.grn_entity_type !== 'INVENTORY_TRANSFER');

  return (
    <div className="document-header">
      <div className="ant-row">
        {selectedGRN?.tenant_seller_info?.seller_name && (
          <div className="ant-col-md-24">
            <H3Text
              text={(
                <span>
                  <UserOutlined />
                  {' '}
                  <span>
                    <Link
                      to={`/vendors/view/${selectedGRN?.seller_id}`}
                      target="_blank"
                    >
                      {`${selectedGRN?.internal_slr_code} - ${selectedGRN?.tenant_seller_info?.seller_name}`}
                    </Link>
                  </span>
                </span>
              )}
              className="document-header__party-name"
            />
          </div>
        )}
        <div className="ant-col-md-12">
          <Tooltip title={selectedGRN?.tenant_info?.tenant_name}>
            <div className="document-header__field">
              <H3Text text="Location" className="document-header__field-name" />
              <H3Text text={selectedGRN?.tenant_info?.tenant_name?.length > 25 ? `${selectedGRN?.tenant_info?.tenant_name?.slice(0, 25)}..` : selectedGRN?.tenant_info?.tenant_name} className="document-header__field-value" />
            </div>
          </Tooltip>
        </div>
        <div className="ant-col-md-12">
          <Tooltip title={selectedGRN?.tenant_department_info?.department_name}>
            <div className="document-header__field">
              <H3Text text="Department" className="document-header__field-name" />
              <H3Text text={selectedGRN?.tenant_department_info?.department_name?.length > 25 ? `${selectedGRN?.tenant_department_info?.department_name?.slice(0, 25)}..` : selectedGRN?.tenant_department_info?.department_name} className="document-header__field-value" />
            </div>
          </Tooltip>
        </div>
        {selectedGRN?.goods_received_from && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text text="Transfer From" className="document-header__field-name" />
              <H3Text
                text={selectedGRN?.goods_received_from}
                className="document-header__field-value"
              />
            </div>
          </div>
        )}
        <div className="ant-col-md-12">
          <div className="document-header__field">
            <H3Text text="GRN Date" className="document-header__field-name" />
            <H3Text
              text={selectedGRN?.grn_date_time ? (toISTDate(selectedGRN?.grn_date_time).format('DD/MM/YYYY')) : '-'}
              className="document-header__field-value"
            />
          </div>
        </div>
        {selectedGRN?.grn_entity_type === 'PURCHASE_ORDER' && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text text="Purchase Order#" className="document-header__field-name" />
              {selectedGRN?.linked_pos?.length === 1 ? (
                <H3Text
                  text={<Link to={`/approval?type=po&id=${selectedGRN?.linked_pos?.[0]?.po_id}`} target="_blank">{selectedGRN?.linked_pos?.[0].po_number}</Link>}
                  className="document-header__field-value"
                />) : (
                (() => {
                  const firstPos = selectedGRN?.linked_pos?.[0];
                  const remainingPos = selectedGRN?.linked_pos?.slice(1) || [];
                  const remainingCount = remainingPos.length;
                  return firstPos ? (
                    <Tooltip
                      title={
                        remainingCount > 0 ? (
                          <div className="tally-locations-tooltip">
                            {remainingPos?.map((po, index) => (
                              <Link
                                key={index}
                                to={`/approval?type=po&id=${po?.po_id}`}
                                target="_blank"
                                style={{
                                  fontWeight: 600,
                                  color: '#1890ff',
                                  fontSize: '13px',
                                  display: 'block',
                                  marginBottom: '4px',
                                }}
                              >
                                {po?.po_number}
                              </Link>
                            ))}
                          </div>
                        ) : (
                          <div className="tally-locations-tooltip">No additional Pos</div>
                        )
                      }
                      placement="top"
                      overlayInnerStyle={{ background: 'white', color: 'black' }}
                    >
                      <span >
                        <Link
                          to={`/approval?type=po&id=${firstPos.po_id}`}
                          target="_blank"
                          className="document-header__field-value"
                        >
                          {firstPos.po_number}
                        </Link>
                        <span
                          style={{
                            color: 'gray'
                          }}
                        >
                          {remainingCount > 0 && ` +${remainingCount} more`}
                        </span>
                      </span>
                    </Tooltip>
                  ) : (
                    <span>-</span>
                  );
                })()
              )}
            </div>
          </div>
        )}
        {selectedGRN?.invoice_date && (
          <div className="ant-col-md-12">
            <div className="document-header__field document-header__date">
              <H3Text text="Invoice Date" className="document-header__field-name" />
              {quickUpdateDisable ? (
                <div className="document-header__field-input">
                  <DatePicker
                    value={updateInvoiceDate}
                    onChange={(value) => {
                      if (!user?.tenant_info?.purchase_config?.sub_modules?.goods_receiving_note?.settings?.allow_grn_date_before_invoice_date && dayjs(value).isBefore(dayjs(selectedGRN?.grn_date_time))) {
                        notification.error({
                          message: 'Invoice date cannot be earlier than GRN Date.',
                          placement: 'top',
                          duration: '4'
                        });
                      } else {
                        setState({ updateInvoiceDate: value });
                        quickUpdateGRN({
                          invoice_date: value,
                          grn_id: selectedGRN.grn_id,
                        });
                      }
                    }}
                    format="DD-MMM-YYYY"
                    allowClear={false}
                  />
                </div>
              ) : (
                <H3Text
                  text={selectedGRN?.invoice_date ? (toISTDate(selectedGRN?.invoice_date).format('DD/MM/YYYY')) : '-'}
                  className="document-header__field-value"
                />
              )}
            </div>
          </div>
        )}
        {selectedGRN?.conversion_rate && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text text="Conversion Rate" className="document-header__field-name" />
              <H3Text text={`${selectedGRN?.conversion_rate}`} className="document-header__field-value" />
            </div>
          </div>
        )}
        {selectedGRN?.tenant_seller_info?.gst_number && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text text="GSTIN" className="document-header__field-name" />
              {(isGstNumberEditable) && (
                <div className="document-header__field-input ">
                  <input
                    value={updatedGstNumber}
                    onChange={(e) => {
                      setState({ updatedGstNumber: e.target.value });
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        setIsGstNumberEditable(false);
                        quickUpdateGRN({
                          gst_number: updatedGstNumber || null,
                          grn_id: selectedGRN.grn_id,
                        });
                      }
                    }}
                    className="document-custom-input"
                    maxLength="15"
                  />
                  &nbsp;
                  <CheckOutlined
                    style={{ color: '#1890ff', cursor: 'pointer' }}
                    onClick={() => {
                      setIsGstNumberEditable(false);
                      quickUpdateGRN({
                        gst_number: updatedGstNumber || null,
                        grn_id: selectedGRN.grn_id,
                      });
                    }}
                  />
                </div>
              )}
              {(!isGstNumberEditable) && (
                <div className="document-header__field-value">
                  {updatedGstNumber && (
                    <H3Text
                      text={updatedGstNumber}
                    />
                  )}
                  {(quickUpdateDisable && !accountingGSTTransactionAsPerMaster) && (
                    <EditFilled
                      className="edit-icon"
                      onClick={() => {
                        setIsGstNumberEditable(true);
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        {selectedGRN?.invoice_number && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text text="Vendor Invoice#" className="document-header__field-name" />
              {(isInvoiceNumber) && (
                <div className="document-header__field-input">
                  <input
                    value={updateInvoiceNumber}
                    onChange={(e) => {
                      setState({ updateInvoiceNumber: e.target.value });
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        setIsInvoiceNumber(false);
                        quickUpdateGRN({
                          invoice_number: updateInvoiceNumber,
                          grn_id: selectedGRN.grn_id,
                        }, () => {
                          setState({ isUserReadyForPrefillData: true });
                        }, () => {
                          setState({ isUserReadyForPrefillData: false });
                        });
                      }
                    }}
                    className="document-custom-input"
                  />
                  &nbsp;
                  <CheckOutlined
                    style={{ color: '#1890ff', cursor: 'pointer' }}
                    onClick={() => {
                      setIsInvoiceNumber(false);
                      quickUpdateGRN({
                        invoice_number: updateInvoiceNumber || null,
                        grn_id: selectedGRN.grn_id,
                      }, () => {
                        setState({ isUserReadyForPrefillData: true });
                      }, () => {
                        setState({ isUserReadyForPrefillData: false });
                      });
                    }}
                  />
                </div>
              )}
              {(!isInvoiceNumber) && (
                <div className="document-header__field-value">
                  {updateInvoiceNumber && (
                    <H3Text
                      text={
                        textTruncate({ text: selectedGRN?.invoice_number, truncateTextLength: 25, enableTooltip: true })
                      }
                    />
                  )}
                  {quickUpdateDisable && (
                    <EditFilled
                      className="edit-icon"
                      onClick={() => {
                        setIsInvoiceNumber(true);
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="ant-col-md-12">
          <div className="document-header__field">
            <H3Text text="e-Way Bill#" className="document-header__field-name" />

            {(isEWayBillNumber) && (
              <div className="document-header__field-value">
                <input
                  value={updateEWayBillNumber}
                  onChange={(e) => {
                    setState({ updateEWayBillNumber: e.target.value });
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      setIsEWayBillNumber(false);
                      quickUpdateGRN({
                        e_way_bill_number: updateEWayBillNumber,
                        grn_id: selectedGRN.grn_id,
                      }, () => {
                        getActivityLog(selectedGRN?.grn_id, 'good_receiving_note');
                        setState({ isUserReadyForPrefillData: true });
                      }, () => {
                        setState({ isUserReadyForPrefillData: false });
                      });
                    }
                  }}
                  className="document-custom-input"
                />
                &nbsp;
                <CheckOutlined
                  style={{ color: '#1890ff', cursor: 'pointer' }}
                  onClick={() => {
                    setIsEWayBillNumber(false);
                    quickUpdateGRN({
                      e_way_bill_number: updateEWayBillNumber || null,
                      grn_id: selectedGRN.grn_id,
                    }, () => {
                      getActivityLog(selectedGRN?.grn_id, 'good_receiving_note');
                      setState({ isUserReadyForPrefillData: true });
                    }, () => {
                      setState({ isUserReadyForPrefillData: false });
                    });
                  }}
                />
              </div>
            )}
            {(!isEWayBillNumber) && (
              <div className="document-header__field-value">
                {updateEWayBillNumber && (
                  <H3Text text={selectedGRN?.e_way_bill_number} />
                )}
                {quickUpdateDisable && (
                  <EditFilled
                    className="edit-icon"
                    onClick={() => {
                      setIsEWayBillNumber(true);
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        <div className="ant-col-md-12">
          <div className="document-header__field document-header__date">
            <H3Text text="Due Date" className="document-header__field-name" />

            {quickUpdateDisable ? (
              <div className="document-header__field-input">
                <DatePicker
                  value={updateDueDate}
                  onChange={(value) => {
                    setState({ updateDueDate: value });
                    quickUpdateGRN({
                      grn_due_date: value,
                      grn_id: selectedGRN.grn_id,
                    });
                  }}
                  format="DD-MMM-YYYY"
                  allowClear={false}
                />
              </div>
            ) : (
              <H3Text
                text={selectedGRN?.grn_due_date ? (toISTDate(selectedGRN?.grn_due_date).format('DD/MM/YYYY')) : '-'}
                className="document-header__field-value"
              />
            )}
          </div>
        </div>
        {
          selectedGRN && (
            <div className="ant-col-md-12">
              <div className="document-header__field">
                <H3Text text="e-Way Bill" className="document-header__field-name" />
                {(isEWayBillAttachment) && (
                  <div className="document-header__field-value ">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChangeEWayBill}
                      className="document-custom-input"
                    />
                    &nbsp;
                    <CheckOutlined
                      style={{ color: '#1890ff', cursor: 'pointer' }}
                      onClick={() => {
                        setIsEWayBillAttachment(false);
                        const payload = {
                          e_way_bill: [{
                            url: eWayBillAttachment?.response?.location,
                            type: eWayBillAttachment?.response?.contentType,
                            name: eWayBillAttachment?.response?.originalname,
                          }],
                          grn_id: selectedGRN.grn_id,
                        };
                        quickUpdateGRN(payload, () => {
                          getActivityLog(selectedGRN?.grn_id, 'good_receiving_note');
                          setState({ isUserReadyForPrefillData: true });
                        }, () => {
                          setState({ isUserReadyForPrefillData: false });
                        });
                      }}
                    />
                  </div>
                )}
                {(!isEWayBillAttachment) && (
                  <div className="document-header__field-value">
                    <H3Text
                      text={
                        <a
                          href={selectedGRN?.e_way_bill?.[0]?.url} target="_blank" rel="noreferrer">{
                            textTruncate({
                              text: selectedGRN?.e_way_bill?.[0]?.name,
                              truncateTextLength: 25,
                              enableTooltip: true,
                            })
                          }
                        </a>
                      }
                      style={{
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: selectedGRN?.e_way_bill?.[0]?.name ? '90%' : '',
                      }}
                    />
                    {quickUpdateDisable && (
                      <EditFilled
                        className="edit-icon"
                        onClick={() => {
                          setIsEWayBillAttachment(true);
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        }
        {user?.tenant_info?.seller_rating_config?.is_active && (
          <div className="ant-col-md-12">
            <div
              className="document-header__field"
              onMouseEnter={() => {
                document.getElementById('editIcon').style.display = 'block';
              }}
              onMouseLeave={() => {
                document.getElementById('editIcon').style.display = 'none';
              }}
            >
              <H3Text text="GRN Rating" className="document-header__field-name" />
              <H3Text
                text={(
                  <div style={{ display: 'flex', cursor: 'pointer', alignItems: 'center' }} onClick={() => setState({ showModal: true, isUpdateRating: true })}>
                    <div className={`rating-container-update ${getRatingColor(selectedGRN?.rating_info?.rating)}`} style={{ width: selectedGRN?.rating_info?.rating > 0 ? '40px' : '85px' }}>
                      <div className="rating-container-main">
                        <span className="rating-value">{selectedGRN?.rating_info?.rating > 0 ? selectedGRN?.rating_info?.rating?.toFixed(1) : 'Rate Now'}</span>
                        <span className="rating-star">&#9733;</span>
                      </div>
                    </div>
                    <div style={{ margin: '2px 0px 0px 4px' }}>
                      <EditOutlined
                        id="editIcon"
                      />
                    </div>
                  </div>
                )}
                className="document-header__field-value"
              />
            </div>
          </div>
        )}
        {selectedGRN?.integration_busy_config?.status === 'CONNECTED' && !isApInvoiceEnabled && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text text="Busy STPT Account" className="document-header__field-name" />
              <div className="document-header__field-input">
                <PRZSelect
                  showSearch
                  placeholder="select account"
                  value={selectedGRN?.busy_stpt_name}
                  options={selectedGRN?.integration_busy_config?.purchase_type_master_list.map((__k) => ({
                    value: __k.name,
                    label: __k.name,
                  }))}
                  loading={quickUpdateGRNLoading}
                  size="small"
                  optionFilterProp="children"
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  onChange={(value) => {
                    quickUpdateGRN({
                      busy_stpt_name: value,
                      grn_id: selectedGRN.grn_id,
                    }, () => getActivityLog(selectedGRN?.grn_id, 'good_receiving_note'));
                  }}
                  disabled={!quickUpdateDisable}
                />
              </div>
            </div>
          </div>
        )}
        {selectedGRN?.it_id && selectedGRN?.integration_tally_config?.purchase_account_selection === 'FROM_GRN' && !isApInvoiceEnabled && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text text="Tally purchase Account" className="document-header__field-name" />
              <div className="document-header__field-input">
                <PRZSelect
                  showSearch
                  placeholder="select account"
                  value={selectedGRN?.tally_purchase_account}
                  options={selectedGRN?.integration_tally_config?.purchase_account_list?.map((__k) => ({
                    value: __k.purchase_account_name,
                    label: __k.purchase_account_name,
                  }))}
                  loading={quickUpdateGRNLoading}
                  size="small"
                  optionFilterProp="children"
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  onChange={(value) => {
                    quickUpdateGRN({
                      tally_purchase_account: value,
                      grn_id: selectedGRN.grn_id,
                    }, () => getActivityLog(selectedGRN?.grn_id, 'good_receiving_note'));
                  }}
                  disabled={!quickUpdateDisable}
                />
              </div>
            </div>
          </div>
        )}
        {selectedGRN?.it_id && selectedGRN?.integration_tally_config?.purchase_voucher_type_selection === 'DOC_LEVEL' && !isApInvoiceEnabled && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text text="Tally Voucher Type" className="document-header__field-name" />
              <div className="document-header__field-input">
                <PRZSelect
                  showSearch
                  placeholder="select account"
                  value={selectedGRN?.purchase_voucher_type}
                  options={selectedGRN?.integration_tally_config?.voucher_types?.map((__k) => ({
                    value: __k.voucher_type_name,
                    label: __k.voucher_type_name,
                  }))}
                  loading={quickUpdateGRNLoading}
                  size="small"
                  optionFilterProp="children"
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  onChange={(value) => {
                    quickUpdateGRN({
                      purchase_voucher_type: value,
                      grn_id: selectedGRN.grn_id,
                    }, () => getActivityLog(selectedGRN?.grn_id, 'good_receiving_note'));
                  }}
                  disabled={!quickUpdateDisable}
                />
              </div>
            </div>
          </div>
        )}
        {
          user?.tenant_info?.zoho_branch_id && selectedGRN?.seller_id && selectedGRN?.zoho_bill_id && !isApInvoiceEnabled && (
            <div className="ant-col-md-12">
              <div className="document-header__field">
                <H3Text text="Zoho Status" className="document-header__field-name" />
                <H3Text
                  text={(
                    <a href={`https://books.zoho.in/app#/bills/${selectedGRN?.zoho_bill_id}`} target="_blank" rel="noreferrer">
                      <span className="status-tag">Pushed</span>
                      <LinkOutlined />
                    </a>
                  )}
                  className="document-header__field-value"
                />
              </div>
            </div>
          )
        }
        {
          isTallyConnected && !isApInvoiceEnabled && (
            <div className="ant-col-md-12">
              <div className="document-header__field">
                <H3Text text="Tally Status" className="document-header__field-name" />
                {(selectedGRN?.tally_updated_at && !selectedGRN?.tally_warning_message) && (
                  <H3Text
                    text={(
                      <React.Fragment>
                        <span className="status-tag">Pushed</span>
                      </React.Fragment>
                    )}
                    className="document-header__field-value"
                  />
                )}
                {selectedGRN?.tally_warning_message && (
                  <H3Text
                    text={(
                      <React.Fragment>
                        <span
                          className="status-tag"
                          style={{
                            backgroundColor: 'rgb(255, 0, 0)',
                          }}
                        >
                          Error
                        </span>
                        &nbsp;
                        <Tooltip
                          title={selectedGRN?.tally_warning_message}
                        >
                          <InfoCircleOutlined style={{ color: 'rgb(255, 0, 0)', cursor: 'pointer' }} />
                        </Tooltip>
                      </React.Fragment>
                    )}
                    className="document-header__field-value"
                  />
                )}
                {(!selectedGRN?.tally_voucher_id && !selectedGRN?.tally_warning_message && !selectedGRN?.tally_updated_at) && (
                  <H3Text
                    text={(
                      <React.Fragment>
                        <span
                          className="status-tag"
                          style={{
                            backgroundColor: 'rgb(70, 139, 247)',
                          }}
                        >
                          Not Synced
                        </span>
                      </React.Fragment>
                    )}
                    className="document-header__field-value"
                  />
                )}
              </div>
            </div>
          )
        }
        {
          selectedGRN?.integration_busy_config?.status === 'CONNECTED' && ['ISSUED'].includes(selectedGRN?.status) && !isApInvoiceEnabled && (
            <div className="ant-col-md-12">
              <div className="document-header__field">
                <H3Text text="Busy Status" className="document-header__field-name" />
                {selectedGRN?.busy_voucher_id && (
                  <H3Text
                    text={(
                      <React.Fragment>
                        <span className="status-tag">Pushed</span>
                      </React.Fragment>
                    )}
                    className="document-header__field-value"
                  />
                )}
                {selectedGRN?.busy_sync_error && (
                  <H3Text
                    text={(
                      <React.Fragment>
                        <span
                          className="status-tag"
                          style={{
                            backgroundColor: 'rgb(255, 0, 0)',
                          }}
                        >
                          Error
                        </span>
                        &nbsp;
                        <Tooltip
                          title={selectedGRN?.busy_sync_error}
                        >
                          <InfoCircleOutlined style={{ color: 'rgb(255, 0, 0)', cursor: 'pointer' }} />
                        </Tooltip>
                      </React.Fragment>
                    )}
                    className="document-header__field-value"
                  />
                )}
                {(!selectedGRN?.busy_voucher_id && !selectedGRN?.busy_sync_error) && (
                  <H3Text
                    text={(
                      <React.Fragment>
                        <span
                          className="status-tag"
                          style={{
                            backgroundColor: 'rgb(70, 139, 247)',
                          }}
                        >
                          Not Synced
                        </span>
                      </React.Fragment>
                    )}
                    className="document-header__field-value"
                  />
                )}
              </div>
            </div>
          )
        }
        {selectedGRN?.subcontractor_mos?.length > 0 && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text text="Sub Contractor MO" className="document-header__field-name" />
              <H3Text
                text={
                  selectedGRN?.subcontractor_mos?.length == 1 ? (
                    <Link to={`/production/manufacturing-orders/view/${selectedGRN?.subcontractor_mos[0]?.mo_id}`} key={selectedGRN?.subcontractor_mos[0]?.mo_id} target='_blank' style={{ 'color': '#2D7DF7' }}>
                      {selectedGRN?.subcontractor_mos[0]?.mo_number}
                    </Link>
                  ) : (
                    <Tooltip overlay={(
                      <div
                        style={{
                          color: 'rgb(0, 0, 0)',
                          fontSize: '12px',
                          fontWeight: '500',
                          padding: '2px',
                        }}
                      >
                        {selectedGRN?.subcontractor_mos?.map(item => {
                          <Link to={`/production/manufacturing-orders/view/${item?.mo_id}`} key={item?.mo_id} target='_blank' style={{ 'color': '#2D7DF7' }}>{item?.mo_number}</Link>;
                        })}
                      </div>
                    )}
                    color="rgb(234, 242, 254)"
                    overlayStyle={{ zIndex: 9999 }}>
                      {`${selectedGRN?.subcontractor_mos[0]?.mo_number}... +${selectedGRN?.subcontractor_mos?.length - 1} more`}
                    </Tooltip>
                  )
                }
                className="document-header__field-value"
              />
            </div>
          </div>
        )}
        {selectedGRN?.custom_fields?.length > 0 ? (
          <ViewCustomFields
            customFields={selectedGRN?.custom_fields?.filter((cf) => cf.is_active)}
            wrapperClass="document-header__field"
            labelClass="document-header__field-name"
            valueClass="document-header__field-value"
            inputClassName="document-header__field-input"
            isHorizontal
            isEditable={quickUpdateDisable}
            handleCustomFieldUpdate={quickUpdateGRN}
            entityId={selectedGRN.grn_id}
            callback={() => getGRNById(Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.GOOD_RECEIVING, Helpers.permissionTypes.READ).join(','), selectedGrnId || match?.params?.grnId)}
            entityName="grn_id"
            entityType="GOOD_RECEIVING_NOTES"
          />
        ) : ''}
      </div>
    </div>
  );
};

export default ViewGRNDetails;